import { useRef, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { useTranslation } from 'react-i18next';
import { SELECT_CUSTOM_STYLE } from '../../resources/constants';
import { fetchCustomers } from './customerCenterApi';
import type { BranchId, Customer } from './types';

const PICKER_DEBOUNCE_MS = 300;

export interface CustomerOption {
  value: number;
  label: string;
  customer: Customer;
  isSuggested?: boolean;
}

export interface CustomerDropdownProps {
  branchId: BranchId;
  excludeIds?: number[];
  disabled?: boolean;
  customer: Customer | null;
  /** Highlighted first option when nothing is saved; never auto-selected. */
  suggestedCustomer?: Pick<Customer, 'id' | 'name'> | null;
  ariaLabel: string;
  placeholder?: string;
  onPick: (customer: Customer | null) => void;
}

export default function CustomerDropdown({
  branchId,
  excludeIds = [],
  disabled = false,
  customer,
  suggestedCustomer = null,
  ariaLabel,
  placeholder,
  onPick,
}: CustomerDropdownProps) {
  const { t } = useTranslation('scenes.customerCenter');
  const [failed, setFailed] = useState(false);
  const pending = useRef<{ timer: number; controller: AbortController; resolve: (options: CustomerOption[]) => void } | null>(null);

  const toOption = (option: Customer, isSuggested = false): CustomerOption => ({
    value: Number(option.id),
    label: isSuggested
      ? t('splitSuggestedOption', { name: option.name, id: option.id })
      : t('splitCustomerOption', { name: option.name, id: option.id }),
    customer: option,
    isSuggested,
  });

  const suggestedOption = (): CustomerOption | null => {
    if (!suggestedCustomer || customer) return null;
    const sid = Number(suggestedCustomer.id);
    if (!Number.isFinite(sid) || excludeIds.includes(sid)) return null;
    return toOption(
      { id: suggestedCustomer.id, name: suggestedCustomer.name, active: true },
      true,
    );
  };

  const mergeSuggested = (options: CustomerOption[]): CustomerOption[] => {
    const hint = suggestedOption();
    if (!hint) return options;
    const rest = options.filter((opt) => opt.value !== hint.value);
    return [hint, ...rest];
  };

  const loadOptions = (inputValue: string): Promise<CustomerOption[]> => {
    const { promise, resolve } = Promise.withResolvers<CustomerOption[]>();
    if (pending.current) {
      window.clearTimeout(pending.current.timer);
      pending.current.controller.abort();
      pending.current.resolve([]);
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      fetchCustomers(branchId, inputValue.trim(), 1, 10, controller.signal, false)
        .then((page) => {
          setFailed(false);
          resolve(mergeSuggested(page.items.map((item) => toOption(item))));
        })
        .catch(() => {
          if (!controller.signal.aborted) setFailed(true);
          resolve([]);
        });
    }, PICKER_DEBOUNCE_MS);
    pending.current = { timer, controller, resolve };
    return promise;
  };

  return (
    <AsyncSelect<CustomerOption, false>
      aria-label={ariaLabel}
      value={customer ? toOption(customer) : null}
      isClearable
      isDisabled={disabled}
      defaultOptions
      loadOptions={loadOptions}
      filterOption={(candidate) => !excludeIds.includes(candidate.data.value)}
      onChange={(option) => onPick(option?.customer ?? null)}
      placeholder={placeholder ?? t('splitSearch')}
      noOptionsMessage={() => (failed ? t('splitLoadFailed') : t('splitNoMatch'))}
      menuPortalTarget={document.body}
      formatOptionLabel={(option, { context }) => (
        <span className={context === 'menu' && option.isSuggested ? 'fw-semibold text-primary' : undefined}>
          {option.label}
        </span>
      )}
      styles={{
        ...SELECT_CUSTOM_STYLE('#fff'),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        option: (base, state) => ({
          ...base,
          backgroundColor: state.data.isSuggested ? '#e7f1ff' : base.backgroundColor,
        }),
      }}
    />
  );
}
